import { Injectable } from '@angular/core';
import { LocalStorage } from 'src/utils/local-storage';

@Injectable({
  providedIn: 'root'
})
export class DatalayerService {

  private lastEventKey: string | null = null;
  private lastEventTs = 0;
  private isResizing = false;
  private windowAdViewEventListener = null;

  constructor(private localStorage: LocalStorage) {}

  public resizing(isResizing: boolean): void {
    this.isResizing = isResizing;
  }

  public getResizing(): boolean {
    return this.isResizing;
  }

  public loginSuccess(): void {
    const userProfile = this.localStorage.getUserProfile();
    if (!userProfile) {
        console.log("No user profile");
        return;
    } 

    const userLang = LocalStorage.getPreferredLang(); 

    const eventPayload = {
      event: "trackEvent-hubLoginSuccess",
      event_name: "hublogin",
      login_status: "success",
      language: userLang,
      billing_id: userProfile.billingid,
      subscriber_id: userProfile.subscriberId,
      subscriber_type: userProfile.membertype || "NA",
      profession_type: userProfile.profession || "NA",
      industry: userProfile.industry || "NA",
      package_code: userProfile.subscriptionInfo?.[0]?.packageCode || "NA"
    };

    this.pushToDataLayer(eventPayload);
  }

  public hubHomePageView(): void {

    if (this.isResizing) {
      return;
    }

    const userProfile = this.localStorage.getUserProfile();
    if (!userProfile) {
        console.log("No user profile");
        return;
    } 

    const userLang = LocalStorage.getPreferredLang(); 

    const eventPayload = {
      event: "trackEventSPA-PageView",
      event_name: "page_view",
      language: userLang,
      page_type: "hubhomepage",
      page_title: "Hub Home Page",
      page_referrer: document.referrer || "",
      billing_id: userProfile.billingid,
      subscriber_type: userProfile.membertype || "NA",
      subscriber_id: userProfile.subscriberId,
      profession_type: userProfile.profession || "NA",
      industry: userProfile.industry || "NA",
      package_code: userProfile.subscriptionInfo?.[0]?.packageCode || "NA"
    };

    this.pushToDataLayer(eventPayload);
  }

  public loginFailure(username: string): void {
    const eventPayload = {
        event: "trackEvent-hubLoginFailure",
        event_name: "hublogin",
        login_status: "failure",
        username: username
    };

    this.pushToDataLayer(eventPayload);
  }

  public advertisementViewedEvent(adData: any) {
    const userProfile = this.localStorage.getUserProfile();
    const eventPayload = {
      event: "trackEvent-hubAdImpression",
      event_name: "hub_ad_impression",
      position: adData.type,
      manufacturer_id: adData.manufacturerId,
      manufacturer: adData.manufacturerName,
      ad_campaign: adData.campaign,
      language: adData.language,
      billing_id: userProfile.billingid,
      subscriber_type: userProfile.membertype || "NA",
      subscriber_id: userProfile.subscriberId,
      profession_type: userProfile.profession || "NA",
      industry: userProfile.industry || "NA",
      package_code: userProfile.subscriptionInfo?.[0]?.packageCode || "NA"
    };

    this.pushToDataLayer(eventPayload);
  }

  public advertisementClickedEvent(adData: any) {
    const userProfile = this.localStorage.getUserProfile();
    const eventPayload = {
      event: "trackEvent-hubAdClick",
      event_name: "hub_ad_click",
      position: adData.type,
      manufacturer_id: adData.manufacturerId,
      manufacturer: adData.manufacturerName,
      ad_campaign: adData.campaign,
      language: adData.language,
      billing_id: userProfile.billingid,
      subscriber_type: userProfile.membertype || "NA",
      subscriber_id: userProfile.subscriberId,
      profession_type: userProfile.profession || "NA",
      industry: userProfile.industry || "NA",
      package_code: userProfile.subscriptionInfo?.[0]?.packageCode || "NA"
    };

    this.pushToDataLayer(eventPayload);
  }

  public resourceClick(link: string, linkText: string, title: string): void {

    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "practice_resources",
      subcategory: title,
      link_url: link,
      link_text: linkText
    };

    this.pushToDataLayer(eventPayload);
  }

  public cpjClick(linkText: string, linkUrl: string, articleTitle: string): void {
    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "cpj",
      subcategory: articleTitle || "NA",
      link_text: linkText || "NA",
      link_url: linkUrl || "NA"
    };
    this.pushToDataLayer(eventPayload);
  }

  public drugShortageClick(title: string, url: string): void {

    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "drug_shortages",
      subcategory: title || "NA",    // semantic value based on shortage title
      link_text: title || "NA",
      link_url: url || "NA"
    };

    this.pushToDataLayer(eventPayload);
  }

  public conEdClick(linkText: string, linkUrl: string, courseTitle: string): void {
    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "cpha_learning",
      subcategory: courseTitle || "NA",
      link_text: linkText || "NA",
      link_url: linkUrl || "NA"
    };

    this.pushToDataLayer(eventPayload);
  }

  public eventClick(link: string, linkText: string, title: string): void {

    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "events",
      subcategory: title,
      link_url: link,
      link_text: linkText
    };

    this.pushToDataLayer(eventPayload);
  }


  private pushToDataLayer(payload: any): void {
    if (!(window as any).dataLayer) {
      (window as any).dataLayer = [];
    }
    
    (window as any).dataLayer.push(payload);
  }

  public hubClick(category: string, subcategory: string, linkText: string, linkUrl: string) : void {
    const userProfile = this.localStorage.getUserProfile();

    if(!userProfile) {
      return;
    }

    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: category,
      subcategory: subcategory || category,
      link_text: linkText || "NA",
      link_url: linkUrl || "NA",
      billing_id: userProfile.billingid,
      subscriber_id: userProfile.subscriberId,
      subscriber_type: userProfile.membertype || "NA",
      profession_type: userProfile.profession || "NA",
      industry: userProfile.industry || "NA",
      package_code: userProfile.subscriptionInfo?.[0]?.packageCode || "NA"
    };

    this.pushToDataLayer(eventPayload);

  }

  public loginPageView() : void{

    const userLang = LocalStorage.getPreferredLang();

    const eventPayload = {
      event: "trackEventSPA-PageView",
      event_name: "page_view",
      page_type: "hublogin", 
      language: userLang
    };

     // Prevent duplicate page_view
    const eventKey = JSON.stringify(eventPayload);
    const now = Date.now();

    if (this.lastEventKey === eventKey && (now - this.lastEventTs) < 2000) {
      return;
    }

    this.lastEventKey = eventKey;
    this.lastEventTs = now;
      
    this.pushToDataLayer(eventPayload);
  }

  public goToCPSClickHeader(link: string, linkText: string): void {

    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "hubheader",
      subcategory: "go_to_cps",
      link_url: link,
      link_text: linkText
    };

    this.pushToDataLayer(eventPayload);
  }

  public goToCPSClickTile(link: string, linkText: string): void {

    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "go_to_cps",
      subcategory: "go_to_cps",
      link_url: link,
      link_text: linkText
    };

    this.pushToDataLayer(eventPayload);
  }
  
  public footerClick(subcategory: string, link: string, linkText: string): void {
    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "hubfooter",
      subcategory: subcategory,
      link_url: link,
      link_text: linkText
    };

    this.pushToDataLayer(eventPayload);
  }

  public insightsClick(link: string): void {
    const eventPayload = {
      event: "trackEvent-hubClick",
      event_name: "hub_click",
      category: "insights",
      subcategory: "insights", 
      link_url: link,
      link_text: "Submit"
    };

    this.pushToDataLayer(eventPayload);
  }

  languageChangedEvent(language): void {
    const eventPayload = {
        event: 'trackEvent-navigationLanguageSwitch',
        event_name: 'language_switch',
        language_changed: language,
        user_properties: {
          language: language
        }
    };
    this.pushToDataLayer(eventPayload);
  }
}